import Utils from 'app/common/util/Utils';
import { TimePeriod, TimeSynchDto } from 'app/model/CORE';
import moment from 'moment';

/**
 * Adapted copy of the common TimeUtils react class
 */
export default class TimeUtils {
    public static readonly SEC_TO_MS = 1000;
    public static readonly MIN_TO_MS = 60000;
    public static readonly HR_TO_MS = 3600000;
    public static readonly DAY_TO_MS = 24 * TimeUtils.HR_TO_MS;

    public static readonly TWELVE_HOURS_IN_MS = 12 * TimeUtils.HR_TO_MS;

    private static TIME_SYNC: TimeSynchDto;

    static copy(d: Date): Date {
        return d ? new Date(d.getTime()) : null;
    }

    static min(d1: Date, ...dates: Date[]) {
        let reply = d1;
        for (const d2 of dates) {
            if (d2 && (!reply || d2.getTime() < reply.getTime())) {
                reply = d2;
            }
        }
        return reply;
    }

    static max(d1: Date, ...dates: Date[]) {
        let reply = d1;
        for (const d2 of dates) {
            if (d2 && (!reply || d2.getTime() > reply.getTime())) {
                reply = d2;
            }
        }
        return reply;
    }

    static nearestUpperBoundary(date: Date, millisec: number): Date {
        const time = date.getTime();
        const val = time % millisec;
        return val === 0 ? date : new Date(time + millisec - val);
    }

    static nearestLowerBoundary(date: Date, millisec: number): Date {
        const time = date.getTime();
        const val = time % millisec;
        return new Date(time - val);
    }

    static nearestUpperMinuteBoundary(date: Date, numMinutes: number): Date {
        return TimeUtils.nearestUpperBoundary(date, numMinutes * TimeUtils.MIN_TO_MS);
    }

    static nearestLowerMinuteBoundary(date: Date, numMinutes: number): Date {
        return TimeUtils.nearestLowerBoundary(date, numMinutes * TimeUtils.MIN_TO_MS);
    }

    // = *

    static withoutTimeOfDay(date: Date | number): Date {
        if (Utils.isNotExists(date)) return undefined;

        const time = TimeUtils.toTimestamp(date);
        const val = time % TimeUtils.DAY_TO_MS;
        return new Date(time - val);
    }

    /**
     * Return the Date that represents 23:59:59 (UTC)
     * @param date
     */
    static endOfDay(date: Date): Date {
        const secondBeforeMidnight = TimeUtils.DAY_TO_MS - TimeUtils.SEC_TO_MS;
        return TimeUtils.addMillis(TimeUtils.withoutTimeOfDay(date), secondBeforeMidnight);
    }

    static timeSinceMidnightInMs(date: Date | number): number {
        if (Utils.isNotExists(date)) return undefined;

        return TimeUtils.toTimestamp(date) % TimeUtils.DAY_TO_MS;
    }

    static dateSinceMidnightInMs(date: Date | number): Date {
        return TimeUtils.toDate(TimeUtils.timeSinceMidnightInMs(date));
    }

    static matchHHMM(value: string, complete: boolean) {
        // check that value matches time from 00:00 to 19:59
        let hhMM = value.match(complete ? /^([0-1]\d):?([0-5]\d)$/ : /^[0-1]?\d?:?[0-5]?\d?$/);
        if (hhMM) return hhMM;

        // check that value matches time from 20:00 to 23:59
        hhMM = value.match(complete ? /^([2][0-3]):?([0-5]\d)$/ : /^[2]?[0-3]?:?[0-5]?\d?$/);
        return hhMM;
    }

    static parseHHMM(value: string, refDay?: Date): Date {
        const hhMM = TimeUtils.matchHHMM(value, true); // Expression to detect valid hhMM and extract values
        if (!hhMM) return undefined;
        let time = new Date(parseInt(hhMM[1]) * TimeUtils.HR_TO_MS + parseInt(hhMM[2]) * TimeUtils.MIN_TO_MS);
        if (Utils.isExists(refDay)) {
            time = TimeUtils.mergeDateAndTime(refDay, time);
            // } else if (TimeUtils.isSimulatedTime()) {
            // 	time = TimeUtils.mergeDateAndTime(TimeUtils.getSimulationTime(), time);
        }
        return time;
    }

    static formatDDHHMM(date: Date): string {
        if (Utils.isNotExists(date)) return undefined;

        const day = Utils.leftPadZero(date.getUTCDate(), 2);
        return day + '-' + TimeUtils.formatHHMM(date);
    }

    static formatmmDDHHMM(date: Date): string {
        if (Utils.isNotExists(date)) return undefined;
        const month = Utils.leftPadZero(date.getUTCMonth() + 1, 2);
        const day = Utils.leftPadZero(date.getUTCDate(), 2);
        return day + '/' + month + '-' + TimeUtils.formatHHMM(date);
    }

    static matchDate(value: string, complete: boolean) {
        // TODO : consider using a package such as https://www.npmjs.com/package/incr-regex-package
        return value.match(
            complete
                ? /^(\d){4}-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])$/
                : /^(\d|$)(\d|$)(\d|$)(\d|$)(-|$)((0|$)([1-9]|$)|(1|$)([0-2]|$))(-|$)(([0-2]|$)([0-9]|$)|(3|$)([0-1]|$))$/,
        );
    }

    static formatDate(date: Date, separator = '-'): string {
        if (Utils.isNotExists(date)) return undefined;

        const year = date.getUTCFullYear();
        const month = Utils.leftPadZero(date.getUTCMonth() + 1, 2);
        const day = Utils.leftPadZero(date.getUTCDate(), 2);

        return year + separator + month + separator + day;
    }

    static formatDateDDMMYYYY(date: Date, separator = '/'): string {
        if (Utils.isNotExists(date)) return undefined;

        const year = date.getUTCFullYear();
        const month = Utils.leftPadZero(date.getUTCMonth() + 1, 2);
        const day = Utils.leftPadZero(date.getUTCDate(), 2);

        return day + separator + month + separator + year;
    }

    static parseDate(str: string): Date {
        if (!TimeUtils.matchDate(str, true)) return undefined; // Date.parse is too lax and will match 2020-02- for example.
        const milli = Date.parse(str); // given date str is ISO, the parse result is browser independent & UTC
        return isNaN(milli) ? null : new Date(milli);
    }

    static formatDateHHMM(date: Date): string {
        return TimeUtils.formatDate(date) + ' ' + TimeUtils.formatHHMM(date);
    }

    static formatHHMM(value: Date | number): string {
        if (Utils.isNotExists(value)) return undefined;

        const time = TimeUtils.timeSinceMidnightInMs(value);
        const hours = Math.trunc(time / TimeUtils.HR_TO_MS);
        const mn = Math.trunc((time - hours * TimeUtils.HR_TO_MS) / TimeUtils.MIN_TO_MS);

        return (hours < 10 ? `0${hours}` : `${hours}`) + ':' + (mn < 10 ? `0${mn}` : `${mn}`);
    }

    static formatHH(value: Date | number): string {
        if (Utils.isNotExists(value)) return undefined;

        const time = TimeUtils.timeSinceMidnightInMs(value);
        const hours = Math.trunc(time / TimeUtils.HR_TO_MS);

        return hours < 10 ? `0${hours}` : `${hours}`;
    }

    static safeFormatHHMM(value: Date | number): string {
        return Utils.isExists(value) ? TimeUtils.formatHHMM(value) : '----';
    }

    static formatHHMMSS(value: Date | number): string {
        if (Utils.isNotExists(value)) return undefined;

        const time = TimeUtils.timeSinceMidnightInMs(value);
        const hours = Math.trunc(time / TimeUtils.HR_TO_MS);
        const mn = Math.trunc((time - hours * TimeUtils.HR_TO_MS) / TimeUtils.MIN_TO_MS);
        const sec = Math.trunc((time - hours * TimeUtils.HR_TO_MS - mn * TimeUtils.MIN_TO_MS) / TimeUtils.SEC_TO_MS);

        return (
            (hours < 10 ? `0${hours}` : `${hours}`) +
            ':' +
            (mn < 10 ? `0${mn}` : `${mn}`) +
            ':' +
            (sec < 10 ? `0${sec}` : `${sec}`)
        );
    }

    static SHORT_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    static SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    static formatDayDDMMYY(date: Date): string {
        if (Utils.isNotExists(date)) return undefined;

        const weekDay = TimeUtils.SHORT_WEEKDAYS[date.getUTCDay() - 1];
        //let weekDay = date.toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })
        const day = Utils.leftPadZero(date.getUTCDate(), 2);
        const month = TimeUtils.SHORT_MONTHS[date.getUTCMonth()];
        //let month = date.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' })

        return weekDay + ' ' + day + ' ' + month;
    }

    static formatDDHHMMSS(date: Date): string {
        if (Utils.isNotExists(date)) return undefined;

        const day = Utils.leftPadZero(date.getUTCDate(), 2);
        return day + '-' + TimeUtils.formatHHMMSS(date);
    }

    static formatDurationHHMM(milliseconds: number): string {
        const prefix = milliseconds < 0 ? '-' : '+';
        milliseconds = Math.abs(milliseconds);
        const hours = Math.trunc(milliseconds / TimeUtils.HR_TO_MS);
        const mn = Math.trunc((milliseconds - hours * TimeUtils.HR_TO_MS) / TimeUtils.MIN_TO_MS);
        return prefix + (hours < 10 ? `0${hours}` : `${hours}`) + 'h' + (mn < 10 ? `0${mn}` : `${mn}`);
    }

    static mergeDateAndTime(day: Date, time: Date): Date {
        return TimeUtils.mergeDateAndTimeMillis(day, TimeUtils.timeSinceMidnightInMs(time));
    }

    static mergeDateAndTimeMillis(day: Date, milliSinceMidnight: number): Date {
        if (!day || Utils.isNotExists(milliSinceMidnight)) return undefined;

        const time = TimeUtils.toTimestamp(day);
        const val = time % TimeUtils.DAY_TO_MS;
        return new Date(time - val + milliSinceMidnight);
    }

    static addMillis(date: Date, msec: number): Date {
        if (!date) return undefined;

        return new Date(TimeUtils.toTimestamp(date) + msec);
    }

    static substractMillis(date: Date, msec: number): Date {
        if (!date) return undefined;

        return new Date(TimeUtils.toTimestamp(date) - msec);
    }

    static addSeconds(date: Date, sec: number): Date {
        if (!date) return undefined;
        return new Date(TimeUtils.toTimestamp(date) + sec * TimeUtils.SEC_TO_MS);
    }

    static addMinutes(date: Date, min: number): Date {
        if (!date) return undefined;
        return new Date(TimeUtils.toTimestamp(date) + min * TimeUtils.MIN_TO_MS);
    }

    static addHours(date: Date, hour: number): Date {
        if (!date) return undefined;
        return new Date(TimeUtils.toTimestamp(date) + hour * TimeUtils.HR_TO_MS);
    }

    static addDays(date: Date, numDays: number): Date {
        if (!date) return undefined;
        return new Date(TimeUtils.toTimestamp(date) + numDays * TimeUtils.DAY_TO_MS);
    }

    static diffInSeconds(d1: Date | number, d2: Date | number) {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        return Math.trunc((TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2)) / TimeUtils.SEC_TO_MS);
    }

    static diffInMinutes(d1: Date | number, d2: Date | number) {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        return Math.trunc((TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2)) / TimeUtils.MIN_TO_MS);
    }

    static diffInHours(d1: Date | number, d2: Date | number) {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        return Math.round((TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2)) / TimeUtils.HR_TO_MS);
    }

    static diffInDays(d1: Date | number, d2: Date | number) {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        return Math.round((TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2)) / TimeUtils.DAY_TO_MS);
    }

    static millescondsToMinutes(millesconds: number) {
        return Math.trunc(millesconds / TimeUtils.MIN_TO_MS);
    }

    /**
     * Return the difference to the upper minute (in terms of magnitude).
     *  1.4  =>  2,  and similarly
     *  -1.4 => -2,
     *
     * @param d1
     * @param d2
     * @return
     */
    static diffInMinutesUpper(d1: Date | number, d2: Date | number): number {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        const diff = TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2);
        if (diff < 0.0) {
            return -Math.ceil(-diff / TimeUtils.MIN_TO_MS);
        } else {
            return Math.ceil(diff / TimeUtils.MIN_TO_MS);
        }
    }

    static diffInMs(d1: Date | number, d2: Date | number) {
        if (Utils.isNotExists(d1) || Utils.isNotExists(d2)) return undefined;
        return TimeUtils.toTimestamp(d1) - TimeUtils.toTimestamp(d2);
    }

    static toDate(date: Date | number): Date {
        if (Utils.isExists(date)) {
            if (typeof date === 'number' && date > 0) return new Date(date);
            if (date instanceof Date) return date;
        }
        return undefined;
    }

    static toTimestamp(date: Date | number): number {
        if (Utils.isExists(date)) {
            if (typeof date === 'number') return date;
            if (date instanceof Date) return date.getTime();
        }
        return undefined;
    }

    /**
     * Compare the two dates, returning
     * less than 0 - if d1 < d2
     * more than 0 - if d1 > d2
     * zero        - d1===d2
     */
    static compare(d1: Date, d2: Date) {
        return d1.getTime() - d2.getTime();
    }

    static compareWithUndefined(d1: Date, d2: Date) {
        if (!d1 && !d2) return 0;
        if (!d1) return 1;
        if (!d2) return -1;

        return TimeUtils.compare(d1, d2);
    }
    static NOW() {
        return TimeUtils.getSimulationTime();
    }
    static TODAY() {
        return TimeUtils.withoutTimeOfDay(this.NOW());
    }
    static TOMORROW() {
        return TimeUtils.addMillis(TimeUtils.TODAY(), TimeUtils.DAY_TO_MS);
    }

    static isEquals(d1: Date, d2: Date) {
        if (d1 === d2) return true;
        if (!d1 || !d2) return false;
        return TimeUtils.compare(d1, d2) === 0;
    }

    static withoutSeconds(date: Date) {
        return this.nearestLowerMinuteBoundary(date, 1);
    }

    static withoutMinutes(date: Date) {
        return this.nearestLowerBoundary(date, TimeUtils.HR_TO_MS);
    }

    static formatTimePeriod(timePeriod: TimePeriod) {
        return this.formatHHMM(timePeriod.from) + ' - ' + this.formatHHMM(timePeriod.to);
    }

    static growToCover(originalTimePeriod: TimePeriod, toIncludeTimePeriod: TimePeriod) {
        if (!originalTimePeriod.from || toIncludeTimePeriod.from.getTime() < originalTimePeriod.from.getTime())
            originalTimePeriod.from = new Date(toIncludeTimePeriod.from.getTime());
        if (!originalTimePeriod.to || toIncludeTimePeriod.to.getTime() > originalTimePeriod.to.getTime())
            originalTimePeriod.to = new Date(toIncludeTimePeriod.to.getTime());
    }

    /**
     * Setup the simulation time synchronization
     * @param timeSync
     */
    static setSimulationTimeSync(timeSync: TimeSynchDto) {
        TimeUtils.TIME_SYNC = timeSync;
    }

    static getSimulationTimeSync(): TimeSynchDto {
        return TimeUtils.TIME_SYNC;
    }

    static isSimulatedTime(): boolean {
        return Utils.isExists(TimeUtils.TIME_SYNC);
    }

    /**
     * Get the current simulation time.
     * If the simulation time is frozen, then this method will always return the frozen time.
     * @return
     */
    static getSimulationTime(): Date {
        if (!TimeUtils.TIME_SYNC) {
            return new Date();
        } else if (TimeUtils.TIME_SYNC.frozenTime) {
            return TimeUtils.TIME_SYNC.frozenTime;
        } else {
            const realTime = new Date();
            return TimeUtils.addMillis(realTime, TimeUtils.TIME_SYNC.timeShiftInMs);
        }
    }

    /**
     * Convert Simulation Time to real Time.
     * If simulation time is frozen then this method will raise an exception.
     * @param simulationTime
     * @return
     */
    static convertSimulationToRealTime(simulationTime: Date): Date {
        if (TimeUtils.TIME_SYNC) {
            if (TimeUtils.TIME_SYNC.frozenTime && simulationTime.getTime() !== TimeUtils.TIME_SYNC.frozenTime.getTime())
                throw new Error('Cannot convert simulation to real time when frozen');
            return TimeUtils.substractMillis(simulationTime, TimeUtils.TIME_SYNC.timeShiftInMs);
        } else {
            return simulationTime;
        }
    }

    /**
     * Convert Real Time to Simulation Time.
     * If simulation time is frozen then this method will raise an exception.
     * @param simulationTime
     * @return
     */
    static convertRealToSimulationTime(realTime: Date): Date {
        if (TimeUtils.TIME_SYNC) {
            if (TimeUtils.TIME_SYNC.frozenTime && realTime.getTime() !== TimeUtils.TIME_SYNC.frozenTime.getTime())
                throw new Error('Cannot convert simulation to real time when frozen');
            return TimeUtils.addMillis(realTime, TimeUtils.TIME_SYNC.timeShiftInMs);
        } else {
            return realTime;
        }
    }

    static getDayMonthTime = (date: Date) => {
        const day = Utils.leftPadZero(date.getUTCDate(), 2);
        const month = moment.utc(date).format('MMM');
        const time = TimeUtils.formatHHMM(date);
        return `${day} ${month} ${time}`;
    };
}
